import { useEffect, useRef, useState } from "react";
import { MagnifyingGlass, CheckCircle, PencilSimple } from "@phosphor-icons/react";
import api from "@/lib/api";

const inputCls = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-ink";

const CONFIG = {
    sf_listview: { path: "/campaigns/sf-listviews", key: "listviews", idField: "list_view_id", placeholder: "Search list views by name…", hasObject: true },
    sf_report: { path: "/campaigns/sf-reports", key: "reports", idField: "report_id", placeholder: "Search reports by name…", hasObject: false },
    sf_campaign: { path: "/campaigns/sf-campaigns", key: "campaigns", idField: "sf_campaign_id", placeholder: "Search Salesforce campaigns by name…", hasObject: false },
};

/**
 * Collapsing combobox for picking a Salesforce List View, Tabular Report, or
 * Campaign as a campaign data source. Opens only while searching, collapses to a
 * compact chip after selection. List Views can be pre-filtered by object.
 */
export function SfSourcePicker({ kind, value, onChange }) {
    const cfg = CONFIG[kind];
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [objects, setObjects] = useState([]);
    const boxRef = useRef(null);
    const debounce = useRef(null);

    const selectedId = value?.[cfg.idField];
    const selectedObject = value?.list_view_object || "";
    const selectedLabel = value?.source_label;

    useEffect(() => {
        if (!cfg.hasObject) return;
        api.get("/campaigns/sf-listview-objects").then(({ data }) => setObjects(data.objects || [])).catch(() => setObjects([]));
    }, [cfg.hasObject]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        if (debounce.current) clearTimeout(debounce.current);
        debounce.current = setTimeout(async () => {
            try {
                const params = { search };
                if (cfg.hasObject && selectedObject) params.sobject = selectedObject;
                const { data } = await api.get(cfg.path, { params });
                setOptions(data[cfg.key] || []);
            } catch (_e) {
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => debounce.current && clearTimeout(debounce.current);
    }, [search, open, cfg.path, cfg.key, cfg.hasObject, selectedObject]);

    useEffect(() => {
        const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);

    const choose = (o) => {
        const patch = { list_view_id: null, list_view_object: cfg.hasObject ? o.object : null, report_id: null, sf_campaign_id: null, phone_column: null, source_label: o.name };
        patch[cfg.idField] = o.id;
        onChange(patch);
        setOpen(false);
        setSearch("");
    };

    // Collapsed "selected" chip.
    if (selectedId && selectedLabel && !open) {
        return (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-ink bg-ink/5 px-3.5 py-2.5" data-testid="sf-source-selected">
                <span className="flex min-w-0 items-center gap-2">
                    <CheckCircle size={16} weight="fill" className="shrink-0 text-emerald-600" />
                    <span className="truncate text-sm font-bold text-ink">{selectedLabel}</span>
                    {cfg.hasObject && selectedObject && <span className="shrink-0 rounded-full bg-stone-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-stone2">{selectedObject}</span>}
                </span>
                <button type="button" data-testid="sf-source-change" onClick={() => setOpen(true)} className="flex shrink-0 items-center gap-1 text-xs font-semibold text-ink hover:underline"><PencilSimple size={13} /> Change</button>
            </div>
        );
    }

    const shown = options.slice(0, 8);
    return (
        <div className="space-y-2" ref={boxRef} data-testid="sf-source-picker">
            {cfg.hasObject && (
                <div>
                    <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Object</label>
                    <select data-testid="sf-object-filter" value={selectedObject} onChange={(e) => onChange({ list_view_object: e.target.value, list_view_id: null, source_label: null })} className={inputCls}>
                        <option value="">All objects</option>
                        {objects.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
            )}
            <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone2" />
                <input
                    data-testid="sf-source-search"
                    value={search}
                    onFocus={() => setOpen(true)}
                    onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
                    placeholder={cfg.placeholder}
                    className={`${inputCls} pl-9`}
                />
            </div>
            {open && (
                <div className="max-h-56 overflow-y-auto rounded-xl border border-stone-200 divide-y divide-stone-100" data-testid="sf-source-list">
                    {loading && <div className="px-3 py-3 text-sm text-stone2">Loading…</div>}
                    {!loading && shown.length === 0 && <div className="px-3 py-3 text-sm text-stone2">No matches found.</div>}
                    {!loading && shown.map((o) => (
                        <button key={o.id} type="button" data-testid={`sf-source-option-${o.id}`} onClick={() => choose(o)} className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-stone-50">
                            <span className="truncate font-semibold">{o.name}</span>
                            {cfg.hasObject && <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-stone2">{o.object}</span>}
                        </button>
                    ))}
                    {!loading && options.length > 8 && <div className="px-3 py-2 text-xs text-stone2">Showing first 8 — keep typing to narrow.</div>}
                </div>
            )}
        </div>
    );
}
